import React, { useState, useEffect } from "react";
import {
  Table,
  TableRow,
  TableBody,
  useTheme,
  Box,
  Typography,
  TableContainer,
  IconButton,
} from "@mui/material";
import VantageListHead from "../../../../../component/ListHead/SignalList";
import {
  Tablecell,
  applySortFilter,
  calculateAndFormatNoAbsInt,
  findBrokerAbb,
  findFundAbbById,
  findStrategy,
  getComparator,
  hashAction,
  hashPosition,
} from "../../../../../component/Methods";
import { useSelector } from "react-redux";
import { selectStrategies , selectFunds, selectBrokers } from "../../../../../Redux/Reducers/selectors";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import CrossingTradesExportButtons from "../../../Components/CrossingTradesExportButtons";

const CrossingTradesTable = ({ tableHeader, crossingTrades }) => {
  const theme = useTheme();
  const funds = useSelector(selectFunds);
  const strategies = useSelector(selectStrategies);
  const brokers = useSelector(selectBrokers);
  const [data, setData] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("ticker");
  const [toggleFilterByCustodian, setToggleFilterByCustodian] = useState(false);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  useEffect(() => {
    const filteredOrders = crossingTrades?.map((order) => {
      const strategy = findStrategy(order?.strategy, strategies);
      const ticker = order?.ticker.stock
        ? `${order?.ticker?.symbol}/${order?.ticker?.stock}`
        : order?.ticker?.symbol;
      const tag = order?.crossingTag;
      let action = hashAction(order?.action) + hashPosition(order?.position);
      const shareCount = order?.shareCount;
      const fund = findFundAbbById(order?.fund, funds);
      const custodian = order?.custodian;
      const broker = findBrokerAbb(order?.broker,brokers);

      if (action === "BuyShort") {
        action = "CoverShort";
      }

      return {
        tradeId: order?.tradeId,
        fund: fund,
        strategy: strategy,
        ticker: ticker,
        custodian: custodian,
        broker: broker,
        action: action,
        shareCount: shareCount,
        tag: tag,
      };
    });

    const groupedData = filteredOrders?.reduce((acc, row) => {
      if (!acc[row?.ticker]) {
        acc[row?.ticker] = [];
      }
      acc[row?.ticker].push(row);
      return acc;
    }, {});

    const rowsWithColors = Object.entries(groupedData)?.reduce(
      (acc, [ticker, rows]) => {
        const baseGrey = 200;
        const greyOffset = 0;

                 rows?.forEach((row, index) => {
           let color;
           if (row.tag === 1) {
             const greyShade = baseGrey + ((index * greyOffset) % 400);
             color = theme.palette.grey[greyShade];
           } else if (row?.tag === 0) {
             color = theme.palette.info.light;
           } else {
             color = theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100];
           }
           acc.push({ ...row, color });
         });
        return acc;
      },
      []
    );

    let filteredRows = rowsWithColors;
    if (toggleFilterByCustodian) {
      // First, find all unique ticker+fund combinations that have at least one "cros" custodian
      const tickerFundPairsWithCros = filteredRows
        .filter((row) => row?.custodian === "cros")
        .map((row) => `${row.ticker}_${row.fund}`); // Create unique identifier

      // Then filter rows to include both "cros" rows and their partner rows
      filteredRows = filteredRows.filter((row) => {
        const rowIdentifier = `${row.ticker}_${row.fund}`;
        return tickerFundPairsWithCros.includes(rowIdentifier);
      });
    }

    const filteredUsers = applySortFilter(
      filteredRows,
      getComparator(order, orderBy)
    );
    setData(filteredUsers);
  }, [order, orderBy, crossingTrades, toggleFilterByCustodian]);

  const Legend = () => (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Box sx={{ display: "flex", alignItems: "center", marginRight: 2 }}>
        <Box
          sx={{
            width: 20,
            height: 20,
            backgroundColor: theme.palette.info.light,
            marginRight: 1,
          }}
        />
        <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>Crossing Custodian & Broker</Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box
          sx={{
            width: 20,
            height: 20,
            backgroundColor: theme.palette.grey[200],
            marginRight: 1,
          }}
        />
        <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>Real Custodian / Crossing Broker</Typography>
      </Box>
    </Box>
  );
  return (
    <Box
      sx={{
        borderRadius: "4px",
        marginTop: "1px",
        width: "100%",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 0,
          flexShrink: 0,
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            marginBottom: 1,
            minHeight: 24,
          }}
        >
          <Legend />
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            marginBottom: 1,
            minHeight: 32,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{ fontSize: "12px", fontWeight: "bold", color: theme.palette.text.primary }}
          >
            ONLY SHOW CROSSING TRADES{" "}
          </Typography>
          <IconButton
            onClick={() =>
              setToggleFilterByCustodian(!toggleFilterByCustodian)
            }
            size="small"
            sx={{ p: 0.25 }}
          >
            {toggleFilterByCustodian ? (
              <ToggleOnIcon sx={{ fontSize: 28, color: "success.main" }} />
            ) : (
              <ToggleOffIcon sx={{ fontSize: 28, color: theme.palette.text.secondary }} />
            )}
          </IconButton>
          </Box>
          <CrossingTradesExportButtons rows={data} tableHeader={tableHeader} />
        </Box>
      </Box>

      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "4px",
          marginTop: "1px",
          width: "100%",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <TableContainer
          sx={{
            flex: "1 1 0px",
            minHeight: 0,
            height: 0,
            maxHeight: "100%",
            overflow: "auto",
            display: "block",
          }}
        >
            <Table
              size="small"
              stickyHeader
              aria-label="a dense table"
            >
              <VantageListHead
                order={order}
                orderBy={orderBy}
                headLabel={tableHeader}
                noStatus={true}
                customizeFont={12}
                rowCount={data.length}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {data?.map((row, index) => (
                  <TableRow
                    key={`${row.tradeId ?? "trade"}-${index}`}
                    sx={{
                      backgroundColor: row.color,
                      "& > .MuiTableCell-root": {
                        paddingTop: "1px",
                        paddingBottom: "0px !important",
                        lineHeight: "1.2",
                        "& .MuiInputBase-input": {
                          padding: "0px 0px !important",
                        },
                        "& .MuiIconButton-root": {
                          padding: "0px",
                        },
                      },
                      "&:hover": {
                        background: theme.palette.action.hover,
                        cursor: "pointer",
                      },
                    }}
                  >
                    <Tablecell
                       align="left"
                       sx={{
                         fontSize: "12px",
                         paddingLeft: "5px",
                         borderLeft: "none",
                         borderRight: "none",
                         color: theme.palette.text.primary,
                         borderColor: theme.palette.divider,
                       }}
                     >
                       {row.strategy}
                     </Tablecell>
                     <Tablecell
                       align="left"
                       sx={{
                         fontSize: "12px",
                         paddingLeft: "5px",
                         borderLeft: "none",
                         borderRight: "none",
                         color: theme.palette.text.primary,
                         borderColor: theme.palette.divider,
                       }}
                     >
                       {row?.custodian}
                     </Tablecell>
                     <Tablecell
                       align="left"
                       sx={{
                         fontSize: "12px",
                         paddingLeft: "5px",
                         borderLeft: "none",
                         borderRight: "none",
                         color: theme.palette.text.primary,
                         borderColor: theme.palette.divider,
                       }}
                     >
                       {row?.broker}
                     </Tablecell>
                     <Tablecell
                       align="left"
                       sx={{
                         fontSize: "12px",
                         paddingLeft: "5px",
                         borderLeft: "none",
                         borderRight: "none",
                         color: theme.palette.text.primary,
                         borderColor: theme.palette.divider,
                       }}
                     >
                       {row?.action}
                     </Tablecell>
                     <Tablecell
                       align="left"
                       sx={{
                         fontSize: "12px",
                         paddingLeft: "5px",
                         borderLeft: "none",
                         borderRight: "none",
                         color: theme.palette.text.primary,
                         borderColor: theme.palette.divider,
                       }}
                     >
                       {row?.ticker?.toUpperCase() || ""}
                     </Tablecell>
                     <Tablecell
                       align="right"
                       sx={{
                         fontSize: "12px",
                         paddingLeft: "5px",
                         borderLeft: "none",
                         borderRight: "none",
                         color: theme.palette.text.primary,
                         borderColor: theme.palette.divider,
                       }}
                     >
                       {row?.shareCount !== undefined && row?.shareCount != null
                         ? row?.shareCount < 0
                           ? `(${Math.abs(row?.shareCount)})`
                           : `${calculateAndFormatNoAbsInt(row?.shareCount)}`
                         : ""}
                     </Tablecell>
                     <Tablecell
                       align="left"
                       sx={{
                         fontSize: "12px",
                         paddingLeft: "5px",
                         borderLeft: "none",
                         borderRight: "none",
                         color: theme.palette.text.primary,
                         borderColor: theme.palette.divider,
                       }}
                     >
                       {row?.fund}
                     </Tablecell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default CrossingTradesTable;
